import { useCallback, useState } from 'react';
import styled from 'styled-components';
import { Check16 } from '@elfsight/icons';
import { Icon } from '@elfsight/lib-icon';
import { Show } from '@elfsight/lib-utils';
import {
  DropdownTriggerButton,
  verticalScrollBar
} from '@elfsight/widget-filters';
import { Popout } from '@elfsight/widget-modal';
import {
  getColorWithAlpha,
  getInvertedColor,
  useWindowKeydown
} from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { useJobFilters } from './JobFilterProvider';

type CurrencyAndPeriodDropdownProps<T> = {
  items: T[];
  selectedItem: T;
  setSelectedItem: (item: T) => void;
  formatItem: (item: T) => string;
};

export function CurrencyAndPeriodDropdown<T extends string>({
  items,
  selectedItem,
  setSelectedItem,
  formatItem
}: CurrencyAndPeriodDropdownProps<T>) {
  const { filtersAndSearchColor, activeFiltersColor, font } = useSettings();
  const { currentFiltersStyle } = useJobFilters();

  const [open, setOpen] = useState(false);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);

  const onCloseRequest = () => setOpen(false);
  useWindowKeydown('Escape', onCloseRequest);

  const onItemClick = useCallback(
    (item: T) => {
      onCloseRequest();
      if (item === selectedItem) {
        return;
      }
      setSelectedItem(item);
    },
    [selectedItem, setSelectedItem]
  );

  const portalContainerElement =
    currentFiltersStyle !== 'inline' && anchorElement
      ? anchorElement
      : undefined;

  return (
    <>
      <AnchorItem ref={setAnchorElement}>
        <DropdownTriggerButton
          buttonStyles={{
            padding: '6px 12px',
            minHeight: 'unset',
            height: '32px',
            width: '100%',
            justifyContent: 'space-between'
          }}
          onClick={() => setOpen(true)}
        >
          {formatItem(selectedItem)}
        </DropdownTriggerButton>
      </AnchorItem>

      <Popout
        show={open}
        onCloseRequest={onCloseRequest}
        portalContainerElement={portalContainerElement}
        anchorElement={anchorElement}
        offsetTop={4}
        contentStyles={{ width: anchorElement?.offsetWidth }}
        closeOnOutsideClick
        font={font}
      >
        <Container
          role="listbox"
          aria-label="Select currency"
          _background={getInvertedColor(filtersAndSearchColor)}
          _scrollbarColor={getColorWithAlpha(filtersAndSearchColor, 0.2)}
        >
          {items.map((item) => {
            return (
              <DropdownItem
                key={item}
                onClick={() => onItemClick(item)}
                _color={filtersAndSearchColor}
              >
                <ItemText>{formatItem(item)}</ItemText>
                <Show when={item === selectedItem}>
                  <Icon
                    component={Check16}
                    size={16}
                    fill={activeFiltersColor}
                  />
                </Show>
              </DropdownItem>
            );
          })}
        </Container>
      </Popout>
    </>
  );
}

const AnchorItem = styled.div`
  flex-basis: 50%;
  position: relative;
  max-width: 186px;
  min-width: 0;
`;

const DropdownItem = styled.div<{ _color: string }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 12px 6px 16px;
  cursor: pointer;
  font-size: 14px;
  line-height: 18px;
  font-weight: 500;
  color: ${({ _color }) => _color};

  &:hover {
    background-color: ${({ _color }) => getColorWithAlpha(_color, 0.05)};
  }
`;

const ItemText = styled.div`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const Container = styled.div<{ _background: string; _scrollbarColor: string }>`
  display: flex;
  flex-direction: column;
  padding: 8px 0px;
  width: 100%;
  max-height: 200px;
  overflow-y: auto;
  border-radius: 8px;
  box-sizing: border-box;
  box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.2);
  background-color: ${({ _background }) => _background};

  ${({ _scrollbarColor }) => verticalScrollBar(_scrollbarColor)}
`;
